import { useEffect, useRef, useState } from "react";
import { API } from "../lib/api";
import { Dialog, DialogContent } from "./ui/dialog";
import { ImageOff } from "lucide-react";

const cache = new Map(); // path -> blobUrl

export function SelfieThumb({ path, size = 56, alt = "" }) {
  const [url, setUrl] = useState(() => (path ? cache.get(path) : null) || null);
  const [err, setErr] = useState(false);
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || visible || url) return;
    const io = new IntersectionObserver(
      (entries) => entries[0].isIntersecting && setVisible(true),
      { rootMargin: "300px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible, url]);

  useEffect(() => {
    if (!path || !visible) return;
    if (cache.has(path)) {
      setUrl(cache.get(path));
      return;
    }
    let cancelled = false;
    const token = localStorage.getItem("ubbs_token");
    fetch(`${API}/files/${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error("img " + r.status);
        return r.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const u = URL.createObjectURL(blob);
        cache.set(path, u);
        setUrl(u);
      })
      .catch(() => !cancelled && setErr(true));
    return () => {
      cancelled = true;
    };
  }, [path, visible]);

  if (!path) {
    return (
      <div
        className="bg-white/70 glass-panel flex items-center justify-center text-neutral-400"
        style={{ width: size, height: size }}
      >
        <ImageOff className="h-4 w-4" />
      </div>
    );
  }

  if (err) {
    return (
      <div
        className="bg-white/70 border border-red-200 flex items-center justify-center text-red-400 text-[10px]"
        style={{ width: size, height: size }}
      >
        ERR
      </div>
    );
  }

  return (
    <>
      <button
        ref={ref}
        type="button"
        onClick={() => setOpen(true)}
        className="glass-panel overflow-hidden hover:opacity-80 transition-opacity"
        style={{ width: size, height: size }}
        data-testid="selfie-thumb"
      >
        {url ? (
          <img src={url} alt={alt} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-white/70 animate-pulse" />
        )}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-xl p-2 max-w-xl">
          {url && (
            <img src={url} alt={alt} className="w-full max-h-[80vh] object-contain" />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
